import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import { PageHeader as Family } from '@beyond-js/ui/react';
import { useI18n } from '@cdn/i18n';
import { OutageNotices } from './outage';

interface Properties {
	/** The H1: what is in view */
	title: ReactNode;
	/** The levels above, from the second level down; routed by the shell */
	crumbs?: Array<{ label: string; href?: string }>;
	/** One status, right after the H1 */
	status?: ReactNode;
	/** One muted line of facts */
	facts?: ReactNode;
	/** What the page is for, at the reading width under the header */
	lead?: ReactNode;
	/** The title line's end: one primary action */
	actions?: ReactNode;
	/** The resource's areas (`Tabs`), under the header */
	tabs?: ReactNode;
	/**
	 * The part of the resource on screen (a section, a job), for a title shared by several addresses: the heading
	 * says it to assistive technology and the document is named after it first (`Jobs · Storefront`)
	 */
	section?: { name: string; document: string };
	/** The H1 is still being read (LOAD-01): the shell neither focuses it nor names the document after it */
	pending?: boolean;
	/** What the document is named after when it is more than the H1 (`Release 12 · Storefront`) */
	named?: string;
}

/**
 * What every page shows right after its header, when anything: the admission notice of a person not yet
 * admitted, provided by the console around its pages
 */
export const Announcement = createContext<ReactNode>(null);

/**
 * The page's one header (decision D52's LR-05), drawn by the family's `PageHeader`: crumbs from the second level
 * down, the H1, one status, a facts line, the primary action centred on the title's line and the tabs below.
 * The heading takes focus after a navigation (see the shell), which finds it by `data-page-title`. The lead is the
 * family's (`lead`, 0.41.0, D191): under the facts line, at the reading measure, so the facts line keeps only the
 * object's properties. The console's announcement follows, then the page's outages, each said once on the H1's edge
 * (decision D163).
 */
export function PageHeader({ title, crumbs, status, facts, lead, actions, tabs, section, pending = false, named }: Properties) {
	const { locale } = useI18n();
	const heading = useRef<HTMLHeadingElement>(null);
	const announcement = useContext(Announcement);
	const document = section?.document ?? named ?? '';

	// Marked before the shell looks for the page's title, in the frame after the navigation
	useLayoutEffect(() => {
		if (pending) heading.current?.removeAttribute('data-page-title');
		else heading.current?.setAttribute('data-page-title', document);
	}, [document, pending]);

	return (
		<>
			<Family
				headingRef={heading}
				title={
					<>
						{title}
						{section && <span className="sr-only">: {section.name}</span>}
					</>
				}
				crumbs={crumbs}
				status={status}
				facts={facts}
				lead={lead}
				actions={actions}
				tabs={tabs}
				labels={Family.labels[locale.code === 'es' ? 'es' : 'en']}
			/>
			{announcement}
			<OutageNotices />
		</>
	);
}
